import type { AdoptRequest } from '../../domain/adopt.js';
import { adoptRecordsFor } from '../../domain/entityTypes.js';

export type InboxStudio = { key: string; title: string | null; gameCount: number };
export type InboxGame = { key: string; studio: string; title: string | null };
export type InboxConfig = { key: string; title: string | null };
/** A game's studio that master already holds — named so its group can be headed by name. */
export type InboxParent = { key: string; title: string | null };
export type InboxData = { studios: InboxStudio[]; games: InboxGame[]; configs: InboxConfig[]; parents: InboxParent[] };

/**
 * One studio's row of the drawer, with the games waiting under it: an inbox studio of its own
 * (`inMaster: false` — adopting it brings the games), or one master already holds (`true` —
 * only its games are adopted).
 */
export type StudioGroup = { key: string; title: string | null; inMaster: boolean; games: InboxGame[] };

const byName = <T extends { key: string; title: string | null }>(a: T, b: T) =>
    (a.title ?? a.key).localeCompare(b.title ?? b.key, undefined, { sensitivity: 'base' }) ||
    a.key.localeCompare(b.key);

/**
 * The games headed by their studio: every inbox studio (games or none), then every other
 * studio a game names, titled from `parents` where master knows it. Groups and games are
 * sorted by name, then key.
 */
export const groupInbox = (data: InboxData): StudioGroup[] => {
    const envOnly = new Map(data.studios.map((s) => [s.key, s]));
    const titles = new Map(data.parents.map((p) => [p.key, p.title]));
    const groups = new Map<string, StudioGroup>();
    for (const s of data.studios) groups.set(s.key, { key: s.key, title: s.title, inMaster: false, games: [] });
    for (const g of data.games) {
        const group =
            groups.get(g.studio) ??
            groups
                .set(g.studio, {
                    key: g.studio,
                    title: envOnly.get(g.studio)?.title ?? titles.get(g.studio) ?? null,
                    inMaster: !envOnly.has(g.studio),
                    games: [],
                })
                .get(g.studio)!;
        group.games.push(g);
    }
    return [...groups.values()].map((group) => ({ ...group, games: [...group.games].sort(byName) })).sort(byName);
};

const hit = (needle: string, key: string, title: string | null) =>
    key.toLowerCase().includes(needle) || (title?.toLowerCase().includes(needle) ?? false);

/** In-memory search over key and title. A studio stays when it, or any game under it, matches. */
export const filterInbox = (data: InboxData, search: string): InboxData => {
    const needle = search.trim().toLowerCase();
    if (!needle) return data;
    const games = data.games.filter((g) => hit(needle, g.key, g.title));
    const studiosWithHits = new Set(games.map((g) => g.studio));
    return {
        studios: data.studios.filter((s) => hit(needle, s.key, s.title) || studiosWithHits.has(s.key)),
        games,
        configs: data.configs.filter((c) => hit(needle, c.key, c.title)),
        parents: data.parents,
    };
};

export type InboxSelection = { studios: Set<string>; games: Set<string>; configs: Set<string> };

/**
 * A ticked selection as adopt requests. `studios` are the ticked group headers: the inbox's own
 * studios go in one request (each brings its games, so a game ticked under one is not asked for
 * twice); a studio master already holds becomes "every game under it", one request each. Then
 * one for the games left over, and one for the configs. Never a request with nothing in it.
 */
export const adoptRequestsFor = (environment: string, selection: InboxSelection, inbox: InboxData): AdoptRequest[] => {
    const envOnly = new Set(inbox.studios.map((s) => s.key));
    const studios = [...selection.studios].filter((k) => envOnly.has(k));
    const held = [...selection.studios].filter((k) => !envOnly.has(k));
    const gamesUnder = (studio: string) => inbox.games.filter((g) => g.studio === studio).map((g) => g.key);
    const implied = new Set(studios.flatMap(gamesUnder));
    const covered = new Set([...implied, ...held.flatMap(gamesUnder)]);
    const loose = [...selection.games].filter((k) => !covered.has(k));
    const out: AdoptRequest[] = [];
    // A ticked studio's own request carries its games too (`studios.adopt` brings them along),
    // so its count is the studio plus what it implies — more than `keys.length` reports.
    if (studios.length > 0)
        out.push({ ...adoptRecordsFor('studio', environment, studios), count: studios.length + implied.size });
    for (const studio of held)
        out.push({ environment, scope: 'studio-games', studio, count: gamesUnder(studio).length });
    if (loose.length > 0) out.push(adoptRecordsFor('game', environment, loose));
    if (selection.configs.size > 0) out.push(adoptRecordsFor('config', environment, [...selection.configs]));
    return out;
};

/** The whole-environment adopt from the drawer's **Adopt all**: one studio's games, or everything. */
export const adoptAllRequest = (
    environment: string,
    studio: string | null | undefined,
    studioGameCount: number,
    total: number,
): AdoptRequest =>
    studio
        ? { environment, scope: 'studio-games', studio, count: studioGameCount }
        : { environment, scope: 'all', count: total };
